import { useState, useRef, useEffect, useCallback } from 'react';
import { CameraCapture } from '@code-burrow/design-system';

/**
 * SearchOverlay — full-screen search overlay with optional external API search.
 *
 * Two modes:
 * 1. Collection search (default) — searches local items, with optional external API fallback
 * 2. Can be used for "add to list" flows by passing different onExternalAction callbacks
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the overlay is visible
 * @param {Function} props.onClose - Close handler
 * @param {Array} props.items - Collection items to search locally
 * @param {Function} props.onSelect - Called with the selected local item
 * @param {Function} [props.renderResult] - Custom renderer for local results: (item) => ReactNode
 * @param {string} [props.placeholder] - Search input placeholder
 * @param {Function} [props.onExternalSearch] - async (query) => { results: [], error? }
 * @param {Function} [props.onExternalAction] - async (externalItem) => void — called when user clicks action button
 * @param {Function} [props.renderExternalResult] - (item, { onAction, isLoading, isDone }) => ReactNode
 * @param {string} [props.externalSearchLabel] - Label for the manual trigger button (e.g. "Search BoardGameGeek")
 * @param {Function} [props.externalItemKey] - (item) => string — extracts unique key from external item
 * @param {Function} [props.renderPostAdd] - (addedItem, { onDone }) => ReactNode — shown after a successful add instead of navigating away
 * @param {Function} [props.renderPreAdd] - (pendingItem, { onConfirm, onCancel }) => ReactNode — intermediate step before add (e.g. season picker for TV shows)
 */
export default function SearchOverlay({
    open, onClose, items = [], onSelect,
    onAddNew, addNewLabel = 'Add a new item',
    renderResult, placeholder = 'Search collection...',
    // New external search props
    onExternalSearch, onExternalAction, renderExternalResult,
    externalSearchLabel = 'Search online',
    externalItemKey = (item) => item.id,
    renderPostAdd,
    renderPreAdd,
    // Tab/mode switching
    tabs, activeTab, onTabChange,
    // Pre-populate the search query on open (e.g. from trending carousel click)
    initialQuery = '',
    // When provided, shows a camera icon that opens CameraCapture for scan/AI identification
    // e.g. 'movies' | 'books' | 'games' | 'boardgames'
    appType,
    // When true, immediately opens the camera as soon as the overlay opens
    // (used when the user taps the camera button in the nav bar)
    openCameraOnMount = false,
}) {
    const [query, setQuery] = useState('');
    const inputRef = useRef(null);

    // External search state
    const [externalResults, setExternalResults] = useState([]);
    const [externalLoading, setExternalLoading] = useState(false);
    const [externalError, setExternalError] = useState(null);
    const [externalTriggered, setExternalTriggered] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [actionDoneIds, setActionDoneIds] = useState(new Set());
    const [postAddItem, setPostAddItem] = useState(null);
    const [preAddItem, setPreAddItem] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const searchCounter = useRef(0);

    // Reset all state when overlay opens/closes
    useEffect(() => {
        if (open) {
            setQuery(initialQuery || '');
            setExternalResults([]);
            setExternalLoading(false);
            setExternalError(null);
            setExternalTriggered(false);
            setActionLoadingId(null);
            setActionDoneIds(new Set());
            setPostAddItem(null);
            setPreAddItem(null);
            setCameraOpen(openCameraOnMount);
            searchCounter.current = 0;
            const t = setTimeout(() => inputRef.current?.focus(), 100);
            return () => clearTimeout(t);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset external results when query or tab changes
    useEffect(() => {
        setExternalResults([]);
        setExternalError(null);
        setExternalTriggered(false);
        setActionDoneIds(new Set());
    }, [query, activeTab]);

    // Prevent body scroll when open + Escape to close
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
            const handleKeyDown = (e) => {
                if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            };
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.body.style.overflow = '';
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [open, onClose]);

    const runExternalSearch = useCallback(async (searchQuery) => {
        if (!onExternalSearch || !searchQuery.trim()) return;
        const thisSearch = ++searchCounter.current;
        setExternalLoading(true);
        setExternalError(null);
        setExternalTriggered(true);
        try {
            const result = await onExternalSearch(searchQuery.trim());
            if (thisSearch !== searchCounter.current) return; // stale
            setExternalResults(result.results || []);
            if (result.error) setExternalError(result.error);
        } catch (err) {
            if (thisSearch !== searchCounter.current) return;
            setExternalError(err.message);
        } finally {
            if (thisSearch === searchCounter.current) setExternalLoading(false);
        }
    }, [onExternalSearch]);

    // Auto-trigger external search when no local results
    // Uses a ref for runExternalSearch to avoid re-triggering on identity changes
    const runExternalSearchRef = useRef(runExternalSearch);
    runExternalSearchRef.current = runExternalSearch;

    useEffect(() => {
        if (!onExternalSearch || !query.trim() || query.trim().length < 2) return;
        // Don't re-trigger if we already fired for this query
        if (externalTriggered) return;

        const normalizedQuery = query.toLowerCase().trim();
        const hasLocalMatches = items.some((item) =>
            item.title?.toLowerCase().includes(normalizedQuery) ||
            item.creator?.toLowerCase().includes(normalizedQuery) ||
            item.director?.toLowerCase().includes(normalizedQuery) ||
            String(item.year || '').includes(normalizedQuery) ||
            item.genres?.some((g) => g.toLowerCase().includes(normalizedQuery))
        );

        // Longer debounce when local results exist so they feel instant;
        // shorter when there's nothing local and the user is waiting.
        const delay = hasLocalMatches ? 1200 : 800;
        const timer = setTimeout(() => runExternalSearchRef.current(query), delay);
        return () => clearTimeout(timer);
    }, [query, items, onExternalSearch, externalTriggered]);

    const handleExternalAction = useCallback(async (item) => {
        if (!onExternalAction) return;
        const key = externalItemKey(item);
        setActionLoadingId(key);
        try {
            const result = await onExternalAction(item);
            // Pre-add flow: show intermediate UI (e.g. season picker) before completing
            if (result && result._preAdd && renderPreAdd) {
                setPreAddItem(result);
                return;
            }
            setActionDoneIds((prev) => new Set(prev).add(key));
            if (result && renderPostAdd) {
                // Show post-add prompt (e.g. TogetherPrompt) instead of navigating
                setPostAddItem(result);
            } else if (result && onSelect) {
                // No post-add renderer — navigate to detail modal after brief delay
                setTimeout(() => {
                    onSelect(result);
                    onClose();
                }, 400);
            }
        } catch (err) {
            setExternalError(err.message);
        } finally {
            setActionLoadingId(null);
        }
    }, [onExternalAction, onSelect, onClose, externalItemKey, renderPostAdd, renderPreAdd]);

    // Called when pre-add step completes (e.g. season picker confirms)
    const handlePreAddConfirm = useCallback(async (addedItem) => {
        if (preAddItem) {
            const key = externalItemKey(preAddItem);
            setActionDoneIds((prev) => new Set(prev).add(key));
        }
        setPreAddItem(null);
        if (addedItem && renderPostAdd) {
            setPostAddItem(addedItem);
        } else if (addedItem && onSelect) {
            setTimeout(() => {
                onSelect(addedItem);
                onClose();
            }, 400);
        }
    }, [preAddItem, externalItemKey, renderPostAdd, onSelect, onClose]);

    const handleScanResult = useCallback(({ query }) => {
        setCameraOpen(false);
        setQuery(query);
        // External search auto-fires via the debounce effect watching `query`
    }, []);

    if (!open) return null;

    const normalizedQuery = query.toLowerCase().trim();
    const localResults = (() => {
        if (!normalizedQuery) return [];
        const matched = items.filter((item) =>
            item.title?.toLowerCase().includes(normalizedQuery) ||
            item.creator?.toLowerCase().includes(normalizedQuery) ||
            item.director?.toLowerCase().includes(normalizedQuery) ||
            String(item.year || '').includes(normalizedQuery) ||
            item.genres?.some((g) => g.toLowerCase().includes(normalizedQuery))
        );
        // Deduplicate by title+subtitle to avoid showing multiple records for same season/item
        const seen = new Set();
        return matched.filter((item) => {
            const key = `${item.title}||${item.subtitle || ''}||${item.year || ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    })();

    const handleSelect = (item) => {
        onSelect(item);
        onClose();
    };

    const handleAddNew = () => {
        onAddNew?.(query.trim());
        onClose();
    };

    const hasExternalSearch = !!onExternalSearch;
    const showExternalSection = hasExternalSearch && (externalTriggered || externalLoading);

    // Default external result renderer
    const defaultRenderExternal = (item, { onAction, isLoading, isDone }) => (
        <>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-text-primary truncate font-body">
                    {item.name || item.title}
                </p>
                {(item.yearPublished || item.year) && (
                    <p className="text-xs text-text-secondary font-body">{item.yearPublished || item.year}</p>
                )}
            </div>
            <div className="flex-shrink-0">
                {isDone ? (
                    <span className="text-xs text-green-500 font-medium">Added</span>
                ) : isLoading ? (
                    <div className="w-4 h-4 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
                ) : (
                    <button
                        onClick={(e) => { e.stopPropagation(); onAction(); }}
                        className="p-1 text-text-tertiary hover:text-accent-primary transition-colors"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                    </button>
                )}
            </div>
        </>
    );

    const renderExt = renderExternalResult || defaultRenderExternal;

    return (
        <>
        {cameraOpen && appType && (
            <CameraCapture
                appType={appType}
                onResult={handleScanResult}
                onClose={() => setCameraOpen(false)}
            />
        )}
        <div className="fixed inset-0 z-[60] bg-bg-primary sm:bg-black/60 sm:backdrop-blur-sm flex flex-col sm:items-center sm:justify-start sm:pt-[10vh]">
            {/* Search header */}
            <div className="bg-surface border-b border-border-subtle sm:rounded-t-xl sm:max-w-xl sm:w-full sm:border sm:border-border-subtle">
                <div className="flex items-center gap-3 px-4 pb-3" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
                    <div className="flex-1 relative">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                        </svg>
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={placeholder}
                            className="w-full pl-10 pr-4 py-3 rounded-xl bg-bg-primary border border-border text-text-primary placeholder-text-tertiary font-body text-sm focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary transition-colors duration-150"
                        />
                    </div>
                    {appType && (
                        <button
                            onClick={() => setCameraOpen(true)}
                            className="hidden sm:flex flex-shrink-0 p-2 min-h-[44px] text-text-secondary hover:text-accent-primary transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary rounded-md"
                            aria-label="Scan or photograph"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
                            </svg>
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="text-text-secondary text-sm font-medium font-body px-2 py-3 min-h-[44px]"
                    >
                        Cancel
                    </button>
                </div>
                {tabs?.length > 1 && (
                    <div className="flex gap-2 px-4 pb-3">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => onTabChange?.(tab.id)}
                                className={[
                                    'px-3 py-1 rounded-full text-xs font-medium font-body transition-colors duration-150',
                                    activeTab === tab.id
                                        ? 'bg-accent-primary text-text-inverse'
                                        : 'bg-bg-tertiary text-text-secondary hover:text-text-primary',
                                ].join(' ')}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto sm:max-w-xl sm:w-full sm:self-center sm:bg-surface sm:rounded-b-xl sm:border-x sm:border-b sm:border-border-subtle sm:max-h-[60vh]">
                {preAddItem && renderPreAdd ? (
                    renderPreAdd(preAddItem, {
                        onConfirm: handlePreAddConfirm,
                        onCancel: () => setPreAddItem(null),
                    })
                ) : postAddItem && renderPostAdd ? (
                    renderPostAdd(postAddItem, {
                        onDone: (navigateToItem) => {
                            if (navigateToItem && onSelect) {
                                onSelect(postAddItem);
                            }
                            setPostAddItem(null);
                            onClose();
                        },
                    })
                ) : normalizedQuery === '' ? (
                    /* Empty state */
                    <div className="flex flex-col items-center justify-center h-full text-text-tertiary">
                        <svg className="h-12 w-12 opacity-40" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                        </svg>
                    </div>
                ) : localResults.length === 0 && !hasExternalSearch ? (
                    /* No local results, no external search configured */
                    <div className="flex flex-col items-center justify-center h-full text-text-tertiary">
                        <p className="text-sm font-body">No results for &ldquo;{query}&rdquo;</p>
                        {onAddNew && (
                            <button
                                onClick={handleAddNew}
                                className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-accent-primary text-text-inverse text-sm font-medium font-body hover:bg-accent-primary/90 transition-colors duration-150"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                                {addNewLabel}
                            </button>
                        )}
                    </div>
                ) : (
                    /* Results list */
                    <div className="flex flex-col">
                        {/* Local collection results */}
                        {localResults.length > 0 && (
                            <ul className="divide-y divide-border-subtle">
                                {localResults.map((item, i) => (
                                    <li key={item.id || item.traktID || item.steamID || i}>
                                        <button
                                            onClick={() => handleSelect(item)}
                                            className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] text-left hover:bg-surface-hover transition-colors duration-150"
                                        >
                                            {renderResult ? renderResult(item) : (
                                                <>
                                                    {item.imageURL && (
                                                        <img
                                                            src={item.imageURL}
                                                            alt={item.title}
                                                            className="w-10 h-14 object-cover rounded flex-shrink-0"
                                                        />
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-text-primary truncate font-body">
                                                            {item.title}
                                                        </p>
                                                        {(item.subtitle || item.year) && (
                                                            <p className="text-xs text-text-secondary font-body">
                                                                {item.year
                                                                    ? `${item.subtitle || ''}${item.year}`
                                                                    : (item.subtitle || '').replace(/[\s\-–]+$/, '')}
                                                            </p>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Legacy onAddNew button (when no external search configured) */}
                        {!hasExternalSearch && onAddNew && localResults.length > 0 && (
                            <button
                                onClick={handleAddNew}
                                className="flex items-center gap-2 px-4 py-3 min-h-[56px] text-sm font-medium font-body text-accent-primary hover:bg-surface-hover transition-colors duration-150 border-t border-border-subtle"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                                {addNewLabel}
                            </button>
                        )}

                        {/* External search results section */}
                        {showExternalSection && (
                            <div className="border-t border-border-subtle">
                                {/* Section header */}
                                {(externalResults.length > 0 || externalLoading) && (
                                    <div className="px-4 pt-3 pb-1">
                                        <p className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                                            {externalSearchLabel}
                                        </p>
                                    </div>
                                )}

                                {/* Loading */}
                                {externalLoading && (
                                    <div className="flex items-center justify-center py-6">
                                        <div className="w-5 h-5 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
                                    </div>
                                )}

                                {/* Error — show the specific cause when the backend provides one
                                    (e.g. "Trakt sign-in expired…"), else a generic fallback. */}
                                {externalError && !externalLoading && (
                                    <div className="px-4 py-6 text-center">
                                        <p className="text-sm text-text-secondary font-body">
                                            {typeof externalError === 'string' && externalError.trim()
                                                ? externalError
                                                : `Couldn’t reach ${externalSearchLabel} right now`}
                                        </p>
                                        <p className="text-xs text-text-tertiary font-body mt-1">Please try again</p>
                                    </div>
                                )}

                                {/* External result items */}
                                {externalResults.length > 0 && (
                                    <ul className="divide-y divide-border-subtle">
                                        {externalResults.map((item, i) => {
                                            const key = externalItemKey(item);
                                            const isLoading = actionLoadingId === key;
                                            const isDone = actionDoneIds.has(key);
                                            return (
                                                <li
                                                    key={key || i}
                                                    role="button"
                                                    tabIndex={!isLoading && !isDone ? 0 : undefined}
                                                    onClick={() => { if (!isLoading && !isDone) handleExternalAction(item); }}
                                                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isLoading && !isDone) { e.preventDefault(); handleExternalAction(item); } }}
                                                    className={[
                                                        'flex items-center gap-3 px-4 py-3 min-h-[56px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-primary',
                                                        !isLoading && !isDone ? 'cursor-pointer hover:bg-surface-hover active:bg-surface-hover/70' : '',
                                                    ].join(' ')}
                                                >
                                                    {renderExt(item, {
                                                        onAction: () => handleExternalAction(item),
                                                        isLoading,
                                                        isDone,
                                                    })}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                {/* No external results */}
                                {!externalLoading && externalTriggered && externalResults.length === 0 && !externalError && (
                                    <div className="px-4 py-6 text-center">
                                        <p className="text-sm text-text-secondary font-body">No results found online for &ldquo;{query}&rdquo;</p>
                                        <p className="text-xs text-text-tertiary font-body mt-1">Try different keywords or check the spelling</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
        </>
    );
}
